import React, { useContext } from "react";
import { useQuery } from "@apollo/client";
import { FormLayout, Select, TextField } from "@shopify/polaris";
import PropsType from "prop-types";

// lib
import { baseHelper } from "lib/helpers";
import { PrivateContext } from "lib/context";
import constant from "lib/constant/constant";
import { GET_PRODUCT_TAGS, GET_PRODUCT_TYPE } from "app/productOld/apollo/queries";
import { GET_CATALOG } from "app/product/apollo/queries";
import { useLocation, useNavigate } from "react-router-dom";

const AdvanceFilters = (props) => {
  const { dataToFetch, setDataToFetch, setTaggedWith, taggedWith, selectedFilter } = props;
  const { cms } = useContext(PrivateContext);
  const navigate = useNavigate();
  const location = useLocation();

  const { data: getProductTagsData } = useQuery(GET_PRODUCT_TAGS);
  const { data: getProductTypeData } = useQuery(GET_PRODUCT_TYPE);
  const { data: catalogData } = useQuery(GET_CATALOG);

  const filterTag = baseHelper.getResponseData(getProductTagsData, constant.gql.GET_PRODUCT_TAGS) || [];
  const filterType = baseHelper.getResponseData(getProductTypeData, constant.gql.GET_PRODUCT_TYPE) || [];
  const productCatalogOption = baseHelper.getResponseData(catalogData, constant.gql.GET_CATALOG) || [];

  const typeArray = (Array.isArray(filterType) && filterType.map((item) => item.label)) || [];

  const collectionsArray = productCatalogOption?.catalog
    ? productCatalogOption.catalog.map((item) => {
        const { title = "", storeCatalogId = 0 } = item || {};
        return {
          value: title,
          label: title,
          id: storeCatalogId.toFixed(1).toString(),
        };
      })
    : [];

  const filterStatus = [
    { label: cms("label.new"), value: cms("label.new").toLowerCase() },
    { label: cms("label.review"), value: cms("label.review").toLowerCase() },
    { label: cms("label.approve"), value: cms("label.approve").toLowerCase() },
    { label: cms("label.reject"), value: cms("label.reject").toLowerCase() },
    { label: cms("label.delete"), value: cms("label.delete").toLowerCase() },
  ];

  // 🔑 Normalize the selected filter to lowercase so all comparisons are consistent.
  const selectedKey = (selectedFilter || "").toString().toLowerCase();

  const handleStatusSelectChange = (value) => {
    setTaggedWith(value);
    baseHelper.setUrl(location, navigate, { filter: value });
  };

  const handleChange = (value) => {
    setTaggedWith(value);
    let updatedData = {};
    if (selectedKey === constant.COLLECTION.toLowerCase()) {
      const match = collectionsArray.find((item) => item.value === value);
      const setCatId = (match && match.id) || "";
      updatedData = { list_cat_id: setCatId };
    }

    const listSearch = baseHelper.setQueryParams(location, { list_search: value, ...updatedData });
    navigate(`${location.pathname}?${listSearch}`);
    const filterApplied = baseHelper.queryParamsFromLocation(location);
    setDataToFetch({ ...dataToFetch, ...filterApplied, ...updatedData });
  };

  // Decide which control to show
  let control = null;

  if (!selectedKey) {
    control = null;
  } else if (selectedKey === constant.TAG.toLowerCase()) {
    control = (
      <Select
        label={baseHelper.ucFirst(cms("label.Select"))}
        onChange={handleChange}
        value={taggedWith}
        placeholder={cms("label.selectTag")}
        options={filterTag.tags || []}
      />
    );
  } else if (selectedKey === constant.TYPE.toLowerCase()) {
    control = (
      <Select
        onChange={handleChange}
        value={taggedWith}
        label={baseHelper.ucFirst(cms("label.Select"))}
        placeholder={cms("label.selectType")}
        options={typeArray}
      />
    );
  } else if (selectedKey === constant.COLLECTION.toLowerCase()) {
    control = (
      <Select
        onChange={handleChange}
        value={taggedWith}
        label={baseHelper.ucFirst(cms("label.Select"))}
        placeholder={cms("label.selectCollection")}
        options={collectionsArray}
      />
    );
  } else if (selectedKey === constant.STATUS.toLowerCase()) {
    control = (
      <Select
        label={baseHelper.ucFirst(cms("common.label.status"))}
        placeholder={cms("common.label.select")}
        onChange={handleStatusSelectChange}
        value={taggedWith}
        options={filterStatus}
      />
    );
  } else {
    // Everything else => free text “like”
    control = <TextField label={cms("common.label.like")} value={taggedWith ?? ""} onChange={handleChange} />;
  }

  return <FormLayout>{control}</FormLayout>;
};

const Filter = (props) => {
  const {
    dataToFetch,
    isLiveConnect = false,
    productList,
    selectedFilter,
    setDataToFetch,
    setSelectedFilter,
    setTaggedWith,
    vendorProductListData,
  } = props;

  const { cms, currentUser } = useContext(PrivateContext);
  const navigate = useNavigate();
  const location = useLocation();

  const filterOptions = isLiveConnect
    ? [{ label: cms("common.label.titleName"), value: constant.TITLES }]
    : [
        { label: cms("common.label.titleName"), value: constant.TITLE },
        { label: cms("label.tag"), value: constant.TAG.toLowerCase() },
        { label: cms("common.label.type"), value: constant.TYPE.toLowerCase() },
        { label: cms("label.collection"), value: constant.COLLECTION.toLowerCase() },
        { label: cms("common.label.sku"), value: constant.SKU },
      ];

  const isVendor = baseHelper.isVendor(currentUser);
  if (!isVendor) {
    filterOptions.push({ label: cms("common.label.vendor"), value: constant.VENDOR });
  }
  if (productList || vendorProductListData) {
    filterOptions.push({ label: cms("label.platform"), value: constant.LIVECONNECTED_PLATFORM });
  }

  const handleSelectChange = (value) => {
    // Normalize the selected filter value to lowercase for consistency
    const normalized = (value === constant.TITLES ? constant.TITLE : value).toString();

    setSelectedFilter(normalized);

    let listFilter = baseHelper.setQueryParams(location, { list_filter: normalized });
    if (normalized !== constant.STATUS.toLowerCase()) {
      setTaggedWith("");
      listFilter = baseHelper.setQueryParams(location, { list_filter: normalized, list_search: "" });
    }
    navigate(`${location.pathname}?${listFilter}`);
    const filterApplied = baseHelper.queryParamsFromLocation(location);
    setDataToFetch({ ...dataToFetch, ...filterApplied });
  };

  return (
    <Select
      label={cms("common.label.showAll")}
      placeholder={cms("common.placeholder.select")}
      value={selectedFilter || ""}
      onChange={handleSelectChange}
      options={filterOptions}
    />
  );
};

AdvanceFilters.defaultProps = {
  dataToFetch: {},
  setDataToFetch: () => {},
  setTaggedWith: () => {},
  taggedWith: "",
  selectedFilter: "",
};

AdvanceFilters.propTypes = {
  dataToFetch: PropsType.shape(PropsType.object),
  setDataToFetch: PropsType.func,
  setTaggedWith: PropsType.func,
  taggedWith: PropsType.string,
  selectedFilter: PropsType.string,
};

Filter.defaultProps = {
  dataToFetch: {},
  setDataToFetch: () => {},
  setTaggedWith: () => {},
  setSelectedFilter: () => {},
  selectedFilter: "",
};

Filter.propTypes = {
  dataToFetch: PropsType.shape(PropsType.object),
  setDataToFetch: PropsType.func,
  setTaggedWith: PropsType.func,
  setSelectedFilter: PropsType.func,
  selectedFilter: PropsType.string,
};

export { AdvanceFilters as default, Filter };
